import client from '@/graphql/client'
import ListOrgMembers from '@/graphql/queries/ListOrgMembers'
import CreateOrgMember from '@/graphql/mutations/CreateOrgMember'
import UpdateOrgMember from '@/graphql/mutations/UpdateOrgMember'
import DeleteOrgMember from '@/graphql/mutations/DeleteOrgMember'

export const orgMembers = {
  namespaced: true,
  state: {
    // Map of orgId -> array of OrgMember
    membersByOrg: {}
  },
  mutations: {
    setOrgMembers (state, { orgId, members }) {
      state.membersByOrg = { ...state.membersByOrg, [orgId]: members }
    },
    addOrUpdateOrgMember (state, member) {
      const existing = state.membersByOrg[member.orgId] || []
      const index = existing.findIndex(m => m.id === member.id)
      if (index >= 0) {
        const updated = [...existing]
        updated[index] = { ...updated[index], ...member }
        state.membersByOrg = { ...state.membersByOrg, [member.orgId]: updated }
      } else {
        state.membersByOrg = { ...state.membersByOrg, [member.orgId]: [...existing, member] }
      }
    },
    removeOrgMember (state, { id, orgId }) {
      const existing = state.membersByOrg[orgId] || []
      state.membersByOrg = {
        ...state.membersByOrg,
        [orgId]: existing.filter(m => m.id !== id)
      }
    },
    clearOrgMembers (state) {
      state.membersByOrg = {}
    }
  },
  getters: {
    getMembersByOrgId: (state) => (orgId) => state.membersByOrg[orgId] || [],
    getCallerRoleInOrg: (state, getters, rootState) => (orgId) => {
      const userId = rootState.userId
      const members = state.membersByOrg[orgId] || []
      const match = members.find(m => m.userId === userId)
      return match ? match.role : null
    },
    isOrgOwner: (state, getters) => (orgId) => {
      return getters.getCallerRoleInOrg(orgId) === 'OWNER'
    },
    // Single source of truth for "what can the caller do in this org", mirroring the
    // backend org-RBAC gate. Pass the org's creator userId (from project/workflow
    // .organisation.userId) so the org creator is covered even before that org's member
    // list is loaded. orgCreatorUserId is optional.
    // FUTURE: when getProject/getWorkflow return an authoritative callerRole, feed that
    // into getCallerRoleInOrg (or pass it here) and these stay correct for every role.
    canWriteInOrg: (state, getters, rootState) => (orgId, orgCreatorUserId = null) => {
      if (rootState.isSuperAdmin || rootState.adminUser) {
        return true
      }
      if (orgCreatorUserId && orgCreatorUserId === rootState.userId) {
        return true
      }
      return ['OWNER', 'MEMBER'].includes(getters.getCallerRoleInOrg(orgId))
    },
    canManageInOrg: (state, getters, rootState) => (orgId, orgCreatorUserId = null) => {
      if (rootState.isSuperAdmin || rootState.adminUser) {
        return true
      }
      if (orgCreatorUserId && orgCreatorUserId === rootState.userId) {
        return true
      }
      return getters.getCallerRoleInOrg(orgId) === 'OWNER'
    }
  },
  actions: {
    async listOrgMembers ({ commit }, { orgId }) {
      try {
        const { listOrgMembers } = await client.query({
          operationName: 'listOrgMembers',
          query: ListOrgMembers,
          variables: { orgId }
        })
        commit('setOrgMembers', { orgId, members: listOrgMembers?.items || [] })
        return listOrgMembers?.items || []
      } catch (e) {
        console.error(e)
        return []
      }
    },

    async createOrgMember ({ commit }, { orgId, email, role = 'MEMBER' }) {
      try {
        const { createOrgMember } = await client.query({
          operationName: 'createOrgMember',
          query: CreateOrgMember,
          variables: { orgId, email, role }
        })
        commit('addOrUpdateOrgMember', createOrgMember)
        commit('snackbar/showMessage', { content: 'Member added' }, { root: true })
        return createOrgMember
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to add member', color: 'error' }, { root: true })
        console.error(e)
        return null
      }
    },

    async updateOrgMember ({ commit }, { id, orgId, role }) {
      try {
        const { updateOrgMember } = await client.query({
          operationName: 'updateOrgMember',
          query: UpdateOrgMember,
          variables: { id, orgId, role }
        })
        commit('addOrUpdateOrgMember', updateOrgMember)
        commit('snackbar/showMessage', { content: 'Role updated' }, { root: true })
        return updateOrgMember
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to update role', color: 'error' }, { root: true })
        console.error(e)
        return null
      }
    },

    async deleteOrgMember ({ commit }, { id, orgId, userId }) {
      try {
        const { deleteOrgMember } = await client.query({
          operationName: 'deleteOrgMember',
          query: DeleteOrgMember,
          variables: { id, orgId, userId }
        })
        commit('removeOrgMember', { id, orgId })
        commit('snackbar/showMessage', { content: 'Member removed' }, { root: true })
        return deleteOrgMember
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to remove member', color: 'error' }, { root: true })
        console.error(e)
        return null
      }
    }
  }
}
